import React, { FC, memo, useMemo, useState } from 'react';
import { Box, Button, SelectChangeEvent, Typography, IconButton, Grid } from '@mui/material';
import CustomMenuItem from '../../custom/CustomMenuItem';
import CustomSelect from '../../custom/CustomSelect';
import LabelWrapper from '../../common/LabelWrapper';
import { FileType } from '../../../models/inputTypes/FileType';
import { useGetProjectsQuery } from '../../../redux/RTK/queries/projectQuery';
import { useHistory, useParams } from 'react-router';
import SimulationFileContainer, {
  SimulationContainerSetter,
} from '../../common/fileComponents/SimulationFileContainer';
import { ModelEnum } from '../../../models/types/ModelEnum';
import { JstNumerical } from '../../../models/inputTypes/JstFields';
import { ReactComponent as Add } from '../../../assets/images/add.svg';
import CloseIcon from '@mui/icons-material/Close';
import { simsFileslInitialObject } from '../../../utils/initialStates/jstInitialStates';
import ButtonFileDialog from '../../common/ButtonFileDialog';
import {
  useChainProjectFileMutation,
  useGetProjectFileCustomMutation,
} from '../../../redux/RTK/mutations/projectFileMutations';
import { openErrorToast } from '../../../redux/slices/appSlice';
import { useAppDispatch } from '../../../redux/store';

const styles = {
  spaceBetweenContainer: {
    flexWrap: 'wrap',
    display: 'flex',
    maxWidth: '730px',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& >:nth-child(n)': {
      my: 1,
    },
  },
  flexStartBox: {
    '& > *': {
      my: 1,
      mr: { xs: 3, lg: 4 },
    },
    display: 'flex',
    justifyContent: 'flex-start',
  },
} as const;

type JstDataSelectionGroupProps = {
  inputState: JstNumerical;
  setInputState: React.Dispatch<React.SetStateAction<JstNumerical>>;
  simulationChangeFile?: (value: any, file?: FileType) => void;
};

const JstDataSelectionGroup: FC<JstDataSelectionGroupProps> = ({ simulationChangeFile, inputState, setInputState }) => {
  const { data, isFetching } = useGetProjectsQuery({});
  const history = useHistory();
  const { name, fileId } = useParams<{ id?: string; name?: string; fileId?: string }>();
  const [chainProjectFile] = useChainProjectFileMutation();
  const dispatch = useAppDispatch();
  const [hoveredIndex, setHoveredIndex] = useState(-1);

  const projectItems = useMemo(() => {
    if (data?.length) {
      return data.map((item, i) => (
        <CustomMenuItem key={i} value={item.id}>
          <Typography variant={'subtitle2'}>{item.name} </Typography>
        </CustomMenuItem>
      ));
    }
  }, [data]);

  const onInputChange = (
    event: SelectChangeEvent<unknown> | React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setInputState({
      ...inputState,
      [event.target.name]: event.target.value,
    });

    if (name && fileId) {
      history.push('/designTools/maris-jst');
    }
  };

  const handleAddFile = () => {
    const simsList = inputState.simsFiles.slice();
    simsList.push(simsFileslInitialObject);
    setInputState({ ...inputState, simsFiles: simsList });
  };

  const handleRemoveFile = (index: number) => {
    const simsList = inputState.simsFiles.slice();
    simsList.splice(index, 1);
    setInputState({ ...inputState, simsFiles: simsList });
  };

  const isAddDisabled = useMemo(() => {
    return inputState.simsFiles.length >= 20;
  }, [inputState.simsFiles]);

  const onSimulationFileChange = ({ loadedSimulation, file }: SimulationContainerSetter) => {
    if (simulationChangeFile && loadedSimulation && file) {
      return simulationChangeFile(loadedSimulation, file);
    }
    setInputState({ ...inputState, simulationSetup: undefined });
  };

  const onDynamicFileChange = async (file: FileType | undefined, fieldName: string, index: number) => {
    const fileName = file?.currentFile?.name;
    const project = file?.currentFile?.project;

    if (fileName && project) {
      try {
        const resMutation: any = await chainProjectFile({
          projectId: project.toString(),
          fileName: fileName,
          file: file,
        });
        if (resMutation.data.status === 'ok') {
          const simsList = inputState.simsFiles.slice();
          simsList[index] = { ...simsList[index], [fieldName]: file };
          setInputState({ ...inputState, simsFiles: simsList });
          return;
        }
      } catch (e) {
        dispatch(openErrorToast(`Error loading simulation file!`));
      }
    }
  };

  const simsInputs = useMemo(() => {
    const firstColumn = inputState.simsFiles.slice(0, 10);
    const secondColumn = inputState.simsFiles.slice(10);
    return (
      <>
        <Grid container spacing={6}>
          <Grid item>
            {firstColumn.map((item, i) => (
              <Box
                key={i}
                display={'flex'}
                alignItems={'center'}
                my={2}
                width={350}
                height={40}
                onMouseOver={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(-1)}
              >
                <Typography variant={'subtitle1'} mr={2}>
                  {i + 1}
                </Typography>
                <ButtonFileDialog
                  typePage={'ALL'}
                  required
                  simFilesExceptScheduler
                  disabled={!inputState.project}
                  projectId={inputState.project}
                  file={item.file}
                  setFile={(file) => onDynamicFileChange(file, 'file', i)}
                />
                {hoveredIndex === i && (
                  <Box>
                    <IconButton onClick={() => handleRemoveFile(i)} color={'info'}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>
            ))}
          </Grid>
          <Grid item>
            {secondColumn.map((item, i) => (
              <Box
                key={i + 10}
                display={'flex'}
                alignItems={'center'}
                my={2}
                width={350}
                height={40}
                onMouseOver={() => setHoveredIndex(i + 10)}
                onMouseLeave={() => setHoveredIndex(-1)}
              >
                <Typography variant={'subtitle1'} mr={2}>
                  {i + 11}
                </Typography>
                <ButtonFileDialog
                  typePage={'ALL'}
                  required
                  simFilesExceptScheduler
                  disabled={!inputState.project}
                  projectId={inputState.project}
                  file={item.file}
                  setFile={(file) => onDynamicFileChange(file, 'file', i + 10)}
                />
                {hoveredIndex === i + 10 && (
                  <Box>
                    <IconButton onClick={() => handleRemoveFile(i + 10)} color={'info'}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>
            ))}
          </Grid>
        </Grid>
      </>
    );
  }, [inputState, hoveredIndex]);

  return (
    <>
      <Box sx={{ ...styles.spaceBetweenContainer }}>
        <Box>
          <LabelWrapper label={'Select project'}>
            <CustomSelect
              required
              value={inputState.project}
              name={'project'}
              onChange={onInputChange}
              minWidth={'132px'}
              isLoading={isFetching}
              defaultValue={''}
              displayEmpty
            >
              <CustomMenuItem value={''}>
                <Typography variant={'subtitle2'}>select project</Typography>
              </CustomMenuItem>
              {projectItems}
            </CustomSelect>
          </LabelWrapper>
        </Box>
        <Box>
          <LabelWrapper label={'Insert simulation setup'}>
            <SimulationFileContainer
              typePage={'JST'}
              modelType={ModelEnum.JST}
              redirectLocation={'/designTools/maris-jst'}
              project={inputState.project}
              file={inputState.simulationSetup}
              setSimulationInputs={onSimulationFileChange}
            />
          </LabelWrapper>
        </Box>
      </Box>
      <Box my={3}>
        <Typography ml={3} variant={'subtitle2'}>
          Simulation setup files
        </Typography>
        {simsInputs}
      </Box>
      <Box my={2} ml={3}>
        <Button
          disabled={isAddDisabled}
          variant={'contained'}
          color={'success'}
          startIcon={<Add />}
          onClick={handleAddFile}
        >
          <Typography color={'info.contrastText'} variant={'subtitle2'}>
            Add file
          </Typography>
        </Button>
      </Box>
    </>
  );
};

export default memo(JstDataSelectionGroup);
